import AppModalContainer from '@nxsys/components/AppModalContainer';
import {
  formatMoney,
  formatMoneyWithDecimals,
} from '@nxsys/utils/number-helper';
import { showCurrencyByCountry } from '@nxsys/utils';
import { HolidayMethodEnum } from '@nxsys/constants/AppEnums';
import { AppState } from '@nxsys/store';
import { Table } from 'antd';
import React from 'react';
import { useSelector } from 'react-redux';

type NmwBreakdownModalData = {
  timesheetValue?: string | number;
  paymentModel?: string;
  margin?: number;
  retainedHolidayPay?: number | null;
  rolledUpHolidayPay?: number | null;
  errors?: Array<{ errorType?: string; [key: string]: any }>;
  irelandValue?: {
    salary?: number;
    taxGross?: number;
    erPRSI?: number;
    nwmRatePaye?: number;
    umbrella?: {
      assignmentValue?: number;
      margin?: number;
      erPension?: number;
      holidayProvision?: number;
      nmwRateUsed?: number;
      nmwHours?: number;
      nmwReckonablePay?: number;
      nmwRequiredRate?: number;
      nmwAge?: number;
    };
    [key: string]: any;
  };
  payeeUmbrellaValue?: {
    totalHours?: number;
    nwmRatePaye?: number;
    nwmRateUmbrella?: number;
    nmwQualifyingPay?: number;
    nmwHours?: number;
    nmwEePensionDeducted?: number;
    nmwEffectivePay?: number;
    isSalarySacrifice?: boolean;
    employeePension?: number;
    avcEEPension?: number;
    taxablePay?: number;
    expensesPaid?: number;
    employersNI?: number;
    appLevy?: number;
    employersPension?: number;
    nmwRate?: number;
    splitSalaryAndBonus?: boolean;
    bonus?: number | null;
    bonusNegative?: number;
    holidayPayMethod?: HolidayMethodEnum | string;
    holidayPayRate?: number;
    retainedHolidayPayErCosts?: number | null;
    rawData?: { rates?: { holidayPayRate?: number } };
    [key: string]: any;
  };
};

type Props = {
  isOpen: boolean;
  toggle: () => void;
  data: NmwBreakdownModalData | null;
};

const NmwBreakdownModal: React.FC<Props> = ({ isOpen, toggle, data }) => {
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const currencySymbol = showCurrencyByCountry(payrollCountry);
  const errorType = data?.errors?.[0]?.errorType;
  const payeeUmbrellaValue = data?.payeeUmbrellaValue;
  const isPaye = data?.paymentModel === 'PAYEE';
  // Ireland writes its calc to irelandValue and leaves payeeUmbrellaValue
  // null, so every row below would read 0 off the UK shape. The presence of
  // the persisted gross-down block is the signal — a draft generated before
  // NX-5932 has no umbrella block and falls through to the legacy rows.
  const ieUmbrella = data?.irelandValue?.umbrella;
  const showIrelandNmwBreakdown = errorType === 'NMW_RATE' && !!ieUmbrella;
  // The top-down NMW reconciliation only maps to the PAYE calc (basic pay −
  // salary-sacrifice pension ÷ NMW hours). Umbrella derives its rate from
  // taxablePay, so keep the legacy single row there to avoid mislabelling.
  const showPayeNmwBreakdown = errorType === 'NMW_RATE' && isPaye;
  const effectiveNmwRate = isPaye
    ? (payeeUmbrellaValue?.nwmRatePaye ?? 0)
    : (payeeUmbrellaValue?.nwmRateUmbrella ?? 0);

  // NMW breakdown values. Prefer the explicit fields persisted by the backend;
  // fall back to deriving them for drafts created before those fields existed.
  const nmwHours =
    payeeUmbrellaValue?.nmwHours ?? payeeUmbrellaValue?.totalHours ?? 0;
  const nmwEePensionDeducted =
    payeeUmbrellaValue?.nmwEePensionDeducted ??
    (payeeUmbrellaValue?.isSalarySacrifice
      ? (payeeUmbrellaValue?.employeePension ?? 0) +
        (payeeUmbrellaValue?.avcEEPension ?? 0)
      : 0);
  const nmwEffectivePay =
    payeeUmbrellaValue?.nmwEffectivePay ?? effectiveNmwRate * nmwHours;
  const nmwQualifyingPay =
    payeeUmbrellaValue?.nmwQualifyingPay ??
    nmwEffectivePay + nmwEePensionDeducted;

  const legacyNmwRateRows = [
    ...(showPayeNmwBreakdown
      ? [
          ...(payeeUmbrellaValue?.age != null
            ? [
                {
                  key: 'employeeAge',
                  label: 'Employee Age',
                  value: payeeUmbrellaValue.age,
                  isRed: false,
                },
              ]
            : []),
          {
            key: 'nmwQualifyingPay',
            label: 'NMW-qualifying pay',
            value: formatMoney(nmwQualifyingPay, currencySymbol),
            isRed: false,
          },
          ...(nmwEePensionDeducted > 0
            ? [
                {
                  key: 'nmwEePension',
                  label: 'Less: Pension (salary sacrifice)',
                  value: `- ${formatMoney(nmwEePensionDeducted, currencySymbol)}`,
                  isRed: false,
                },
              ]
            : []),
          {
            key: 'nmwEffectivePay',
            label: 'Pay subject to NMW',
            value: formatMoney(nmwEffectivePay, currencySymbol),
            isRed: false,
          },
        ]
      : errorType === 'NMW_RATE'
        ? [
            {
              key: 'timesheetValue',
              label: 'Timesheet Value',
              value: formatMoney(Number(data?.timesheetValue ?? 0), currencySymbol),
              isRed: false,
            },
          ]
        : [
            {
              key: 'salaryBasicPay',
              label: 'Salary/Basic Pay',
              value: formatMoney(
                (payeeUmbrellaValue?.nmwRate ?? 0) *
                  (payeeUmbrellaValue?.totalHours ?? 0),
                currencySymbol,
              ),
              isRed: false,
            },
          ]),
    {
      key: 'hours',
      // For the PAYE NMW breakdown, the divisor is the NMW-qualifying hours
      // (excludes tronc/excluded-rate hours) so the row reconciles to the rate.
      label: showPayeNmwBreakdown ? 'NMW-qualifying hours' : 'Hours',
      value: showPayeNmwBreakdown
        ? nmwHours
        : (payeeUmbrellaValue?.totalHours ?? 0),
      isRed: false,
    },
    {
      key: 'nmwRate',
      label: 'Effective NMW Rate',
      value: formatMoneyWithDecimals(effectiveNmwRate, 4, currencySymbol),
      isRed: errorType === 'NMW_RATE',
    },
    ...(showPayeNmwBreakdown
      ? [
          {
            key: 'requiredNmwRate',
            label: 'Required NMW',
            value: formatMoneyWithDecimals(
              payeeUmbrellaValue?.nmwRate ?? 0,
              2,
              currencySymbol,
            ),
            isRed: false,
          },
        ]
      : []),
    ...(errorType === 'NMW_RATE'
      ? [
          ...(Number(data?.retainedHolidayPay ?? 0) > 0
            ? [
                {
                  key: 'retainedHolidayPay',
                  label: 'Holiday Pay Retained',
                  value: formatMoney(Number(data?.retainedHolidayPay ?? 0), currencySymbol),
                  isRed: false,
                },
              ]
            : []),
          ...(payeeUmbrellaValue?.holidayPayMethod ===
            HolidayMethodEnum.RETAINED &&
          Number(payeeUmbrellaValue?.retainedHolidayPayErCosts ?? 0) > 0
            ? [
                {
                  key: 'retainedHolidayPayErCosts',
                  label: 'Holiday Pay - Employer Costs',
                  value: formatMoney(
                    payeeUmbrellaValue?.retainedHolidayPayErCosts ?? 0,
                    currencySymbol,
                  ),
                  isRed: false,
                },
              ]
            : []),
        ]
      : [
          {
            key: 'advancedHolidayPay',
            label: 'Advanced Holiday Pay',
            value: formatMoney(Number(data?.rolledUpHolidayPay ?? 0), currencySymbol),
            isRed: false,
          },
          {
            key: 'holidayPayRate',
            label: 'Holiday Pay Rate',
            value: `${(
              (payeeUmbrellaValue?.holidayPayRate ||
                payeeUmbrellaValue?.rawData?.rates?.holidayPayRate ||
                0) * 100
            ).toFixed(2)}%`,
            isRed: false,
          },
        ]),
    ...(payeeUmbrellaValue?.splitSalaryAndBonus
      ? [
          {
            key: 'bonus',
            label: 'Bonus',
            value: formatMoney(
              errorType === 'BONUS_NEGATIVE'
                ? (payeeUmbrellaValue?.bonusNegative ?? 0)
                : (payeeUmbrellaValue?.bonus ?? 0),
              currencySymbol,
            ),
            isRed: errorType === 'BONUS_NEGATIVE',
          },
        ]
      : []),
    {
      key: 'taxablePay',
      label: 'Taxable Pay',
      value: formatMoney(payeeUmbrellaValue?.taxablePay ?? 0, currencySymbol),
      isRed: false,
    },
  ];

  // Ireland umbrella: the rate is `reckonable pay ÷ NMW-qualifying hours`,
  // and reckonable pay is what survives the gross-down. Showing the deductions
  // that get there is the only way an admin can tell an under-priced
  // assignment from an over-large margin or an inflated hours entry.
  const irelandNmwRateRows = [
    {
      key: 'assignmentValue',
      label: 'Assignment Value',
      value: formatMoney(
        ieUmbrella?.assignmentValue ?? Number(data?.timesheetValue ?? 0),
        currencySymbol,
      ),
      isRed: false,
    },
    ...(Number(ieUmbrella?.margin ?? 0) > 0
      ? [
          {
            key: 'ieMargin',
            label: 'Less: Margin',
            value: `- ${formatMoney(ieUmbrella?.margin ?? 0, currencySymbol)}`,
            isRed: false,
          },
        ]
      : []),
    ...(Number(ieUmbrella?.holidayProvision ?? 0) > 0
      ? [
          {
            key: 'ieHolidayProvision',
            label: 'Less: Holiday Pay Provision',
            value: `- ${formatMoney(
              ieUmbrella?.holidayProvision ?? 0,
              currencySymbol,
            )}`,
            isRed: false,
          },
        ]
      : []),
    ...(Number(data?.irelandValue?.erPRSI ?? 0) > 0
      ? [
          {
            key: 'ieErPrsi',
            label: "Less: Employer's PRSI",
            value: `- ${formatMoney(
              data?.irelandValue?.erPRSI ?? 0,
              currencySymbol,
            )}`,
            isRed: false,
          },
        ]
      : []),
    ...(Number(ieUmbrella?.erPension ?? 0) > 0
      ? [
          {
            key: 'ieErPension',
            label: "Less: Employer's Pension",
            value: `- ${formatMoney(
              ieUmbrella?.erPension ?? 0,
              currencySymbol,
            )}`,
            isRed: false,
          },
        ]
      : []),
    {
      key: 'ieReckonablePay',
      label: 'Pay subject to NMW',
      value: formatMoney(
        ieUmbrella?.nmwReckonablePay ?? data?.irelandValue?.salary ?? 0,
        currencySymbol,
      ),
      isRed: false,
    },
    // Omitted rather than derived on a draft generated before NX-5932
    // persisted it: nmwRateUsed is rounded to 4dp, so pay ÷ rate would print a
    // plausible-looking but wrong hours figure. Regenerating the draft fills it.
    ...(ieUmbrella?.nmwHours != null
      ? [
          {
            key: 'ieNmwHours',
            label: 'NMW-qualifying hours',
            value: ieUmbrella.nmwHours,
            isRed: false,
          },
        ]
      : []),
    {
      key: 'ieNmwRate',
      label: 'Effective NMW Rate',
      value: formatMoneyWithDecimals(
        ieUmbrella?.nmwRateUsed ?? data?.irelandValue?.nwmRatePaye ?? 0,
        4,
        currencySymbol,
      ),
      isRed: true,
    },
    ...(ieUmbrella?.nmwRequiredRate != null
      ? [
          {
            key: 'ieRequiredNmw',
            label:
              ieUmbrella?.nmwAge != null
                ? `Required NMW (age ${ieUmbrella.nmwAge})`
                : 'Required NMW',
            value: formatMoneyWithDecimals(
              ieUmbrella.nmwRequiredRate,
              2,
              currencySymbol,
            ),
            isRed: false,
          },
        ]
      : []),
    {
      key: 'ieTaxablePay',
      label: 'Taxable Pay',
      value: formatMoney(data?.irelandValue?.taxGross ?? 0, currencySymbol),
      isRed: false,
    },
  ];

  const nmwRateRows = showIrelandNmwBreakdown
    ? irelandNmwRateRows
    : legacyNmwRateRows;

  const columns = [
    {
      dataIndex: 'label',
      key: 'label',
      render: (text: string, record: { isRed: boolean }) => (
        <span style={record.isRed ? { color: 'red' } : undefined}>{text}</span>
      ),
    },
    {
      dataIndex: 'value',
      key: 'value',
      render: (val: string | number, record: { isRed: boolean }) => (
        <span style={record.isRed ? { color: 'red' } : undefined}>{val}</span>
      ),
    },
  ];

  return (
    <AppModalContainer
      title="NMW BREACHED ERROR BREAKDOWN"
      isOpen={isOpen}
      toggle={toggle}
      width={800}
      minHeight="0px"
    >
      <Table
        columns={columns}
        dataSource={nmwRateRows}
        pagination={false}
        showHeader={false}
        size="small"
      />
    </AppModalContainer>
  );
};

export default NmwBreakdownModal;
